import type { NIBSubmissionStatus } from '@/types';
import { NIB_STATUS_COLORS, NIB_STATUS_LABELS } from '@/types';
import { LoadingSpinner } from '@components/common/LoadingSpinner';

interface NIBStatusBadgeProps {
  status: NIBSubmissionStatus;
  size?: 'sm' | 'md' | 'lg';
}

export const NIBStatusBadge = ({
  status,
  size = 'md',
}: NIBStatusBadgeProps) => {
  const getStatusStyles = (status: NIBSubmissionStatus) => {
    const color = NIB_STATUS_COLORS[status];

    switch (color) {
      case 'gray':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'blue':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'amber':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'green':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'purple':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'red':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'md':
        return 'px-2.5 py-1 text-sm';
      case 'lg':
        return 'px-3 py-1.5 text-base';
    }
  };

  const showSpinner = status === 'VALIDATING' || status === 'GENERATING';
  const spinnerSize = size === 'sm' ? 'sm' : size === 'lg' ? 'md' : 'sm';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${getStatusStyles(status)}
        ${getSizeClasses()}
      `}
    >
      {showSpinner && (
        <LoadingSpinner
          size={spinnerSize}
          color="gray"
          className="!border-current"
        />
      )}
      {NIB_STATUS_LABELS[status]}
    </span>
  );
};
